import { type AskPunttSearchProgress } from "@mg/schemas/src/christo/askPuntt";
import {
  Buildings,
  CircleNotch,
  ClockCounterClockwise,
  File,
  Globe,
  MagnifyingGlass,
  Ticket,
} from "@phosphor-icons/react";
import { Flex, Text } from "@radix-ui/themes";

interface SearchProgressProps {
  searchProgress: AskPunttSearchProgress[];
}

function SourceIcon({ source }: { source: string }) {
  const s = source.toLowerCase();
  if (s.includes("enterprise"))
    return <Buildings size={14} className="text-puntt-accent-9" />;
  if (s.includes("ticket"))
    return <Ticket size={14} className="text-puntt-pink-9" />;
  if (s.includes("review history"))
    return <ClockCounterClockwise size={14} className="text-puntt-gold-11" />;
  if (s.includes("regulatory") || s.includes("puntt"))
    return <MagnifyingGlass size={14} className="text-puntt-accent-9" />;
  if (s.includes("sharepoint") || s.includes("claims"))
    return <File size={14} className="text-puntt-gold-9" />;
  if (s.includes("code"))
    return <File size={14} className="text-puntt-accent-11" />;
  return <Globe size={14} className="text-malachite-700" />;
}

export function SearchProgress({ searchProgress }: SearchProgressProps) {
  const visibleProgress = searchProgress.filter(
    (s) =>
      s.status === "searching" ||
      (s.status === "done" &&
        ((s.resultCount ?? 0) > 0 || (s.results?.length ?? 0) > 0)),
  );

  if (visibleProgress.length === 0) return null;

  return (
    <div
      data-testid="ask-puntt-search-progress"
      className="space-y-1 rounded-xl border border-puntt-neutral-gray-6 bg-puntt-neutral-gray-2 p-2"
    >
      {visibleProgress.map((source, idx) => {
        const isSearching = source.status === "searching";
        return (
          <Flex
            key={idx}
            align="center"
            gap="2"
            className="rounded-lg px-2.5 py-1.5"
          >
            {isSearching ? (
              <CircleNotch
                size={14}
                className="shrink-0 animate-spin text-puntt-accent-9 motion-reduce:animate-none"
              />
            ) : (
              <SourceIcon source={source.source} />
            )}
            <Text
              size="1"
              className={`flex-1 ${isSearching ? "text-puntt-neutral-gray-11" : "text-puntt-neutral-gray-12"}`}
            >
              {isSearching ? "Searching " : ""}
              {source.source}
            </Text>
            {!isSearching && source.resultCount !== undefined && (
              <Text size="1" className="text-puntt-neutral-gray-9">
                {source.resultCount}{" "}
                {source.resultCount === 1 ? "Result" : "Results"}
              </Text>
            )}
          </Flex>
        );
      })}
    </div>
  );
}
